// React Query フック定義
//
// v1 のデバイス棚卸し画面（`routes/_staff/it-operation-core/device-inventory/`）の撤去に伴い、
// 消費者が消えた 8 hook（詳細 / 進捗 / サマリ / 対象一覧 / セルフレポート一覧 /
// 一括完了 / レポート代行 / 対応メモ更新）を削除した。
// いずれも BE 側で該当 REST が撤去済みで、呼んでも 404 になる状態だった。
//
// → Task 11 でこれら 8 hook を v1 画面復元のため再度追加している（下部の `v1 専用` セクション）。
//
// 束ね（タグ）単位の後継は
// `routes/_staff/it-operation-core/device-inventory-confirmation/-query/`。
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/components/ui/use-toast';
import { handleApiError } from '@/lib/api/error';
import { useTranslation } from '@/lib/i18n';
import { QUERY_PRESETS } from '@/lib/query';
import { bulkCompleteTasks, executeReportProxy, getDeviceInventoryPatternGenerations, getInstancesDetail, getProgressByRequestId, getSummary, listInstanceTagSummaries, listInstancesV1, listSelfReportTargets, listSelfReports, updateActionMemo } from './api';
import { toPatternGenerationMap } from './selector';
import type { BulkCompleteTasksRequest, InstancesDetailQuery, ListInstancesV1Query, ListSelfReportTargetsQuery, ListSelfReportsQuery, ReportProxyRequest } from './type';

/** `tagSummaries` の queryKey で patternId が入る位置（placeholderData の判定に使う） */
const TAG_SUMMARIES_PATTERN_ID_INDEX = 3;

// Query Keys
//
// ⚠️ `patternGenerations()` はパターン追加のミューテーション（`packages/-query` の
// `useCreatePattern` / `useCreatePatternManual`）からも invalidate される。
// 新パターンが世代一覧に載るまで packages 画面は世代を解決できないため。
export const deviceInventoryQueryKeys = {
  all: ['device-inventory'] as const,
  lists: () => [...deviceInventoryQueryKeys.all, 'list'] as const,
  tagSummaries: (patternId: string, params?: {
    page?: number;
    pageSize?: number;
  }) => [...deviceInventoryQueryKeys.lists(), 'tag-summaries', patternId, ...(params ? [params] : [])] as const,
  patternGenerations: () => [...deviceInventoryQueryKeys.all, 'pattern-generations'] as const,
  // ⚠️ v1 専用（凍結）。v1 退役時に削除。
  // merge-base の `deviceInventoryQueryKeys` を `all` 配下に `v1` セグメントを挟んで移植したもの。
  v1: {
    lists: () => [...deviceInventoryQueryKeys.all, 'v1', 'list'] as const,
    list: (patternId: string, params?: {
      page?: number;
      pageSize?: number;
    }) => [...deviceInventoryQueryKeys.v1.lists(), patternId, ...(params ? [params] : [])] as const,
    details: () => [...deviceInventoryQueryKeys.all, 'v1', 'detail'] as const,
    detail: (instanceId: string) => [...deviceInventoryQueryKeys.v1.details(), instanceId] as const,
    instancesDetails: () => [...deviceInventoryQueryKeys.all, 'v1', 'instances-detail'] as const,
    instancesDetail: (patternId: string, requestId: string | number) => [...deviceInventoryQueryKeys.v1.instancesDetails(), patternId, requestId] as const,
    progressByRequestId: (requestId: string) => [...deviceInventoryQueryKeys.all, 'v1', 'progress-by-request-id', requestId] as const,
    tasks: (instanceId: string) => [...deviceInventoryQueryKeys.all, 'v1', 'tasks', instanceId] as const,
    taskCount: (instanceId: string) => [...deviceInventoryQueryKeys.all, 'v1', 'task-count', instanceId] as const,
    selfReports: (query: Partial<ListSelfReportsQuery>) => [...deviceInventoryQueryKeys.all, 'v1', 'self-reports', query] as const,
    selfReportTargets: (query: Partial<ListSelfReportTargetsQuery>) => [...deviceInventoryQueryKeys.all, 'v1', 'self-report-targets', query] as const,
    summary: (requestId: string) => [...deviceInventoryQueryKeys.all, 'v1', 'summary', requestId] as const
  }
};

/**
 * デバイス棚卸し一覧の固まり（タグ）サマリを取得するフック（固まり単位ページング）
 *
 * キャッシュ方針は一覧の標準プリセット（`QUERY_PRESETS.list()`）に合わせる。
 * 他ユーザーの回答で進捗が裏で動くため、鮮度優先の `list` が適切。
 *
 * `placeholderData` はページ送りのちらつき対策。これが無いと取得中は
 * `data === undefined` になり、`AggregateProgressTable` の `Pagination`
 * （`isPending` に関係なく常時マウントされ、テーブルの loader に隠れない）が
 * 「0 件 / 1 ページ中 2 ページ目」を一瞬描く。1 ページ 50 件でページ送りが
 * 増えたぶん目につく。
 *
 * ⚠️ 素の `keepPreviousData` は使わない。パターンを切り替えたときまで前データを
 * 残すと、**別パターンの固まり**が一瞬並ぶ（母集団が別物なので誤読になる）。
 * 前クエリの patternId が同じときだけ引き継ぐ。
 */
export function useListInstanceTagSummaries(args: {
  patternId: string;
  page?: number;
  pageSize?: number;
}) {
  return useQuery({
    ...QUERY_PRESETS.list(),
    queryKey: deviceInventoryQueryKeys.tagSummaries(args.patternId, {
      page: args.page,
      pageSize: args.pageSize
    }),
    queryFn: () => listInstanceTagSummaries(args),
    enabled: !!args.patternId,
    placeholderData: (previousData, previousQuery) => previousQuery?.queryKey[TAG_SUMMARIES_PATTERN_ID_INDEX] === args.patternId ? previousData : undefined
  });
}

/**
 * デバイス棚卸しパターンの世代一覧（patternId → 'v1' | 'v2'）。
 *
 * v1（旧・requestId 軸の 3 Step パターン）と v2（BOE 基盤の束ね画面パターン）の
 * どちらの画面へルーティングするかを判定するために使う。
 */
export function useDeviceInventoryPatternGenerations() {
  return useQuery({
    // 参照一覧としてキャッシュ方針は標準プリセットに合わせる（staleTime 5 分は従来と同値）。
    ...QUERY_PRESETS.list(),
    queryKey: deviceInventoryQueryKeys.patternGenerations(),
    queryFn: getDeviceInventoryPatternGenerations,
    select: toPatternGenerationMap
    // 既存パターンの世代は起動中に変わらない。ただしパターンを「追加」すると一覧に
    // 行が増えるため、追加のミューテーション側で必ず invalidate すること
    // （`packages/-query` の `useCreatePattern` / `useCreatePatternManual`）。
  });
}

// ============================================================================
// ⚠️ v1 専用（凍結）。v1 退役時に削除
//
// merge-base（be2a51ad2b233ae01066cd4da5050a4fb13026a5）の
// `DeviceInventoryOperation/query/index.ts` から復元。Task 12/13 の旧画面（v1）が import する。
// 旧 `useListInstances` は v2 一覧と名前が衝突したため `useListInstancesV1`
// にリネーム。v2 側は #4483 でタグサマリ版に置き換わり削除済み。
// ============================================================================

/**
 * デバイス棚卸しインスタンス一覧を取得するフック（旧 requestId 軸 API）
 */
export function useListInstancesV1(query: ListInstancesV1Query) {
  return useQuery({
    queryKey: deviceInventoryQueryKeys.v1.list(query.patternId || '', {
      page: query.page,
      pageSize: query.pageSize
    }),
    queryFn: () => listInstancesV1(query),
    enabled: !!query.patternId
  });
}

/**
 * タスクを一括完了するフック
 */
export function useBulkCompleteTasks() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: BulkCompleteTasksRequest) => bulkCompleteTasks(request),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: deviceInventoryQueryKeys.all
      });
    }
  });
}
export function useInstancesDetail(patternId: string, query: InstancesDetailQuery) {
  return useQuery({
    queryKey: deviceInventoryQueryKeys.v1.instancesDetail(patternId, query.requestId),
    queryFn: () => getInstancesDetail(patternId, query),
    enabled: !!patternId && !!query.requestId
  });
}
export function useProgressByRequestId(requestId: string) {
  return useQuery({
    queryKey: deviceInventoryQueryKeys.v1.progressByRequestId(requestId),
    queryFn: () => getProgressByRequestId(requestId),
    enabled: !!requestId
  });
}
export function useSummary(requestId: string) {
  return useQuery({
    queryKey: deviceInventoryQueryKeys.v1.summary(requestId),
    queryFn: () => getSummary(requestId),
    enabled: !!requestId
  });
}

/**
 * セルフレポート対象デバイス一覧を取得するフック
 */
export function useSelfReportTargets(query: ListSelfReportTargetsQuery & {
  enabled?: boolean;
}) {
  const {
    enabled,
    ...apiQuery
  } = query;
  return useQuery({
    queryKey: deviceInventoryQueryKeys.v1.selfReportTargets(apiQuery),
    queryFn: () => listSelfReportTargets(apiQuery),
    enabled: enabled !== false && !!apiQuery.requestId
  });
}

/**
 * セルフレポート結果一覧を取得するフック
 */
export function useSelfReports(query: ListSelfReportsQuery & {
  enabled?: boolean;
}) {
  const {
    enabled,
    ...apiQuery
  } = query;
  return useQuery({
    queryKey: deviceInventoryQueryKeys.v1.selfReports(apiQuery),
    queryFn: () => listSelfReports(apiQuery),
    enabled: enabled !== false && !!apiQuery.requestId
  });
}

/**
 * レポート代行を実行するフック
 */
export function useReportProxy(requestId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: ReportProxyRequest) => executeReportProxy(request),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: deviceInventoryQueryKeys.v1.selfReports({
          requestId
        })
      });
      void queryClient.invalidateQueries({
        queryKey: deviceInventoryQueryKeys.v1.selfReportTargets({
          requestId
        })
      });
    }
  });
}

/**
 * 対応メモを更新するフック
 */
export function useUpdateActionMemo(requestId: string) {
  const queryClient = useQueryClient();
  const {
    i18n
  } = useTranslation();
  const {
    t
  } = useTranslation('translation', {
    keyPrefix: 'components.actionMemoDialog'
  });
  return useMutation({
    mutationFn: ({
      deviceOperationTaskId,
      actionMemo
    }: {
      deviceOperationTaskId: string;
      actionMemo: string | null;
    }) => updateActionMemo(deviceOperationTaskId, actionMemo),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: deviceInventoryQueryKeys.v1.selfReports({
          requestId
        })
      });
      toast({
        description: variables.actionMemo === null ? t('deleteSuccess') : t('saveSuccess')
      });
    },
    onError: error => {
      const {
        message
      } = handleApiError(error, {
        language: i18n.language
      });
      toast({
        description: message,
        variant: 'destructive'
      });
    }
  });
}